import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import BuildersInput from "./BuildersInput";
import {
  Box,
  Button,
  Popper,
  Paper,
  ClickAwayListener,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";

const InputFieldDropdown = ({
  options,
  title,
  selectedFilter,
  setSelectedFilter,
  inputValue,
  setInputValue,
}: {
  options: string[];
  title: string;
  selectedFilter: string[];
  setSelectedFilter: React.Dispatch<React.SetStateAction<string[]>>;
  inputValue: string[];
  setInputValue: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempFilter, setTempFilter] = useState<string[]>([]);

  // Sync temp state with props when dropdown opens
  useEffect(() => {
    if (anchorEl) {
      setTempFilter(selectedFilter);
    }
  }, [anchorEl, selectedFilter]);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedFilter([]);
    setInputValue([]);
    setTempFilter([]);
    setAnchorEl(null);
  };

  const handleSave = (event: React.MouseEvent) => {
    setSelectedFilter(tempFilter);
    setInputValue(tempFilter); // Use tempFilter for both since they're the same now
    setAnchorEl(null);
  };

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={handleToggle}
        sx={{
          border: selectedFilter.length > 0 ? "2px solid #1565c0" : "1px solid #bdbdbd",
          borderRadius: "20px",
          px: 1.5,
          py: 0.5,
          fontSize: "0.8rem",
          textTransform: "capitalize",
          color: selectedFilter.length > 0 ? "#1565c0" : "black",
          display: "flex",
          alignItems: "center",
          backgroundColor: selectedFilter.length > 0 ? "#e3f2fd" : "white",
          "&:hover": { border: "1px solid black", backgroundColor: "white" },
          "&:focus": {
            border: "2px solid #1565c0",
            backgroundColor: "#e3f2fd",
            color: "#1565c0",
          },
        }}
      >
        {selectedFilter.length > 0 ? selectedFilter.join(", ") : title}
        {selectedFilter.length > 0 && (
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{ ml: 1, p: 0, color: "#1565c0" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Button>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 999 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              width: { xs: "200px", md: "320px" },
              boxShadow: 3,
              borderRadius: "10px",
              px: "20px",
              py: "10px",
            }}
          >
            <BuildersInput
              options={options}
              selectedFilter={tempFilter}
              setSelectedFilter={setTempFilter}
            />

            <Divider sx={{ mt: 1.5, mb: 1 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Typography
                onClick={handleClear}
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                Clear
              </Typography>
              <Button
                variant="contained"
                sx={{
                  borderRadius: "10px",
                  backgroundColor: "black",
                  color: "white",
                  px: 2,
                  "&:hover": { backgroundColor: "#333" },
                }}
                onClick={handleSave}
              >
                Save
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default InputFieldDropdown;
